import { projectsId, type ProjectsId } from "./Projects";
import { environmentsId, type EnvironmentsId } from "./Environments";
import { templateContentsId, type TemplateContentsId } from "./TemplateContents";
import { recipientType, type default as RecipientType } from "./RecipientType";
import { notificationStatus, type default as NotificationStatus } from "./NotificationStatus";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for notification_logs */
export type NotificationLogsId = string & { __flavor: "NotificationLogsId" };

/**
 * Represents the table public.notification_logs
 * @uniqueConstraints {"notification_logs_pkey":{"name":"notification_logs_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"notification_logs_project_id_idx":{"name":"notification_logs_project_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"project_id","definition":"project_id","predicate":null}]},"notification_logs_environment_id_idx":{"name":"notification_logs_environment_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"environment_id","definition":"environment_id","predicate":null}]},"notification_logs_status_idx":{"name":"notification_logs_status_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"status","definition":"status","predicate":null}]},"notification_logs_recipient_idx":{"name":"notification_logs_recipient_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"recipient","definition":"recipient","predicate":null}]},"notification_logs_created_at_idx":{"name":"notification_logs_created_at_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]},"notification_logs_template_content_id_idx":{"name":"notification_logs_template_content_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"template_content_id","definition":"template_content_id","predicate":null}]}}
 */
export default interface NotificationLogsTable {
  id: ColumnType<NotificationLogsId, NotificationLogsId | undefined, NotificationLogsId>;

  projectId: ColumnType<ProjectsId, ProjectsId, ProjectsId>;

  environmentId: ColumnType<EnvironmentsId, EnvironmentsId, EnvironmentsId>;

  templateContentId: ColumnType<
    TemplateContentsId | null,
    TemplateContentsId | null,
    TemplateContentsId | null
  >;

  recipientType: ColumnType<RecipientType, RecipientType, RecipientType>;

  recipient: ColumnType<string, string, string>;

  status: ColumnType<NotificationStatus, NotificationStatus | undefined, NotificationStatus>;

  errorMessage: ColumnType<string | null, string | null, string | null>;

  variables: ColumnType<unknown | null, unknown | null, unknown | null>;

  rawPayload: ColumnType<unknown | null, unknown | null, unknown | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  sentAt: ColumnType<Date | null, Date | string | null, Date | string | null>;

  providerId: ColumnType<string | null, string | null, string | null>;

  providerName: ColumnType<string | null, string | null, string | null>;
}

export type NotificationLogs = Selectable<NotificationLogsTable>;

export type NewNotificationLogs = Insertable<NotificationLogsTable>;

export type NotificationLogsUpdate = Updateable<NotificationLogsTable>;

export const notificationLogsId = z.string() as unknown as z.Schema<NotificationLogsId>;

export const notificationLogs = z.object({
  id: notificationLogsId,
  projectId: projectsId,
  environmentId: environmentsId,
  templateContentId: templateContentsId.nullable(),
  recipientType: recipientType,
  recipient: z.string(),
  status: notificationStatus,
  errorMessage: z.string().nullable(),
  variables: z.unknown().nullable(),
  rawPayload: z.unknown().nullable(),
  createdAt: z.date(),
  sentAt: z.date().nullable(),
  providerId: z.string().nullable(),
  providerName: z.string().nullable(),
});

export const notificationLogsInitializer = z.object({
  id: notificationLogsId.optional(),
  projectId: projectsId,
  environmentId: environmentsId,
  templateContentId: templateContentsId.optional().nullable(),
  recipientType: recipientType,
  recipient: z.string(),
  status: notificationStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  variables: z.unknown().optional().nullable(),
  rawPayload: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  sentAt: z.date().optional().nullable(),
  providerId: z.string().optional().nullable(),
  providerName: z.string().optional().nullable(),
});

export const notificationLogsMutator = z.object({
  id: notificationLogsId.optional(),
  projectId: projectsId.optional(),
  environmentId: environmentsId.optional(),
  templateContentId: templateContentsId.optional().nullable(),
  recipientType: recipientType.optional(),
  recipient: z.string().optional(),
  status: notificationStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  variables: z.unknown().optional().nullable(),
  rawPayload: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  sentAt: z.date().optional().nullable(),
  providerId: z.string().optional().nullable(),
  providerName: z.string().optional().nullable(),
});

// ── constraints for NotificationLogs ─────────────────────────────────
export const NotificationLogsConstraints = {
  notification_logs_pkey: {
    name: "notification_logs_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  notification_logs_project_id_idx: {
    name: "notification_logs_project_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "project_id", definition: "project_id", predicate: null }],
  },
  notification_logs_environment_id_idx: {
    name: "notification_logs_environment_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "environment_id", definition: "environment_id", predicate: null }],
  },
  notification_logs_status_idx: {
    name: "notification_logs_status_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "status", definition: "status", predicate: null }],
  },
  notification_logs_recipient_idx: {
    name: "notification_logs_recipient_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "recipient", definition: "recipient", predicate: null }],
  },
  notification_logs_created_at_idx: {
    name: "notification_logs_created_at_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
  notification_logs_template_content_id_idx: {
    name: "notification_logs_template_content_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "template_content_id", definition: "template_content_id", predicate: null }],
  },
} as const;
export type NotificationLogsConstraintsType = typeof NotificationLogsConstraints;
